import React, { useEffect } from "react";
import { Z_INDEX } from "../utils/tooltips";
import { ColumnTypeIcon } from "../utils/columnUtils";
import { ordinalLabel } from "../utils/productMode";
import {
  PRODUCT_ANIMATION_MAX_SECONDS,
  PRODUCT_ANIMATION_MIN_SECONDS,
  PRODUCT_GRID_COLUMN_OPTIONS,
  PRODUCT_MODE_MAX_FIELDS,
  PRODUCT_MODE_MAX_IMAGE_POSITIONS,
} from "../constants";

/**
 * Bottom sheet that configures product mode: the layout, which image each row
 * or tile shows, how dense the grid is, and which columns appear as fields.
 */

const SectionLabel = ({ children, hint, isDarkMode }) => (
  <div className="flex items-baseline justify-between mb-1.5">
    <span className={`text-[10px] font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>{children}</span>
    {hint && <span className={`text-[10px] tabular-nums ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}>{hint}</span>}
  </div>
);

const Chip = ({ selected, onClick, disabled = false, isDarkMode, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected}
    className={`inline-flex items-center gap-1.5 max-w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
      selected
        ? isDarkMode
          ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
          : "bg-blue-50 text-blue-600 border border-blue-200"
        : isDarkMode
        ? "bg-white/[0.04] text-gray-400 border border-white/[0.06] hover:bg-white/[0.08] hover:text-gray-300"
        : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

const Segmented = ({ options, value, onChange, isDarkMode, ariaLabel }) => (
  <div
    role="radiogroup"
    aria-label={ariaLabel}
    className={`inline-flex p-0.5 rounded-lg ${isDarkMode ? "bg-white/[0.04] border border-white/[0.06]" : "bg-gray-100 border border-gray-200"}`}
  >
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 ${
            selected
              ? isDarkMode
                ? "bg-white text-black shadow"
                : "bg-white text-gray-900 shadow-sm"
              : isDarkMode
              ? "text-gray-400 hover:text-white"
              : "text-gray-500 hover:text-gray-900"
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const Switch = ({ checked, onChange, label, isDarkMode }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="w-full flex items-center justify-between gap-3 py-1"
  >
    <span className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>{label}</span>
    <span
      className={`relative inline-flex shrink-0 h-5 w-9 rounded-full transition-colors duration-200 ${
        checked ? "bg-blue-500" : isDarkMode ? "bg-white/[0.12]" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </span>
  </button>
);

// On/off, duration and a preview button for one of the full-screen animations.
const AnimationSection = ({ title, description, switchLabel, rangeLabel, playLabel, settings, onUpdate, onPlay, isDarkMode }) => {
  const seconds = settings.durationSec;
  const percent = ((seconds - PRODUCT_ANIMATION_MIN_SECONDS) / (PRODUCT_ANIMATION_MAX_SECONDS - PRODUCT_ANIMATION_MIN_SECONDS)) * 100;
  const track = isDarkMode ? "rgba(255,255,255,0.1)" : "#e5e7eb";
  const bodyText = isDarkMode ? "text-gray-300" : "text-gray-700";

  return (
    <section>
      <SectionLabel isDarkMode={isDarkMode} hint={settings.enabled ? `${seconds}s` : "Off"}>
        {title}
      </SectionLabel>
      <p className="text-[11px] leading-relaxed mb-1 text-gray-500">{description} Made for recordings; remembered on this device.</p>
      <Switch checked={settings.enabled} onChange={(value) => onUpdate({ enabled: value })} label={switchLabel} isDarkMode={isDarkMode} />
      <div className="mt-2 flex items-center gap-3">
        <span className={`text-xs shrink-0 ${bodyText}`}>Duration</span>
        <input
          type="range"
          min={PRODUCT_ANIMATION_MIN_SECONDS}
          max={PRODUCT_ANIMATION_MAX_SECONDS}
          step={1}
          value={seconds}
          onChange={(e) => onUpdate({ durationSec: Number(e.target.value) })}
          aria-label={rangeLabel}
          className="slider flex-1 h-1 rounded-lg appearance-none cursor-pointer"
          style={{ background: `linear-gradient(to right, #22d3ee 0%, #22d3ee ${percent}%, ${track} ${percent}%, ${track} 100%)` }}
        />
        <span className={`w-8 text-right text-xs tabular-nums ${bodyText}`}>{seconds}s</span>
      </div>
      <button
        type="button"
        onClick={() => onPlay()}
        className={`mt-2.5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors duration-150 ${
          isDarkMode
            ? "bg-white/[0.04] border-white/[0.06] text-gray-300 hover:text-white hover:bg-white/[0.08]"
            : "bg-gray-50 border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        }`}
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path d="M6.3 4.2a1 1 0 011.5-.86l7 4.6a1 1 0 010 1.7l-7 4.6a1 1 0 01-1.5-.85V4.2z" />
        </svg>
        {playLabel}
      </button>
    </section>
  );
};

const CheckIcon = () => (
  <svg className="w-3 h-3 shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const ProductModeSettings = ({
  isOpen,
  onClose,
  isDarkMode,
  settings,
  updateSettings,
  onReset,
  imageColumns,
  fieldColumns,
  activeFields,
  maxImagesPerRow,
  introSettings,
  updateIntroSettings,
  onPlayIntro,
  exportSettings,
  updateExportSettings,
  onPlayExport,
}) => {
  // Close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const positionCount = Math.min(Math.max(maxImagesPerRow || 0, 1), PRODUCT_MODE_MAX_IMAGE_POSITIONS);
  const selectedPosition = Math.min(settings.imageIndex, positionCount - 1);
  const positionOptions = Array.from({ length: positionCount }, (_, i) => ({ value: i, label: ordinalLabel(i) }));

  const activeIds = activeFields.map((col) => col.id);
  const atMaxFields = activeIds.length >= PRODUCT_MODE_MAX_FIELDS;

  const toggleField = (column) =>
    updateSettings((current) => {
      // `null` means "auto" (the price column); materialize it before editing.
      const currentIds = current.fieldIds ?? activeIds;
      if (currentIds.includes(column.id)) {
        return { ...current, fieldIds: currentIds.filter((id) => id !== column.id) };
      }
      if (currentIds.length >= PRODUCT_MODE_MAX_FIELDS) return current;
      return { ...current, fieldIds: [...currentIds, column.id] };
    });

  const mutedText = isDarkMode ? "text-gray-500" : "text-gray-500";
  const bodyText = isDarkMode ? "text-gray-300" : "text-gray-700";
  const isGrid = settings.layout === "grid";

  return (
    <div
      className="fixed inset-0 flex items-end justify-center sm:items-center"
      style={{ zIndex: Z_INDEX.DROPDOWN }}
      role="dialog"
      aria-modal="true"
      aria-label="Product mode settings"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />

      {/* Sheet */}
      <div
        className={`relative w-full max-w-md max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl sm:mx-4 border shadow-2xl animate-slide-up ${
          isDarkMode ? "bg-[#111113] border-white/[0.08] shadow-black/60" : "bg-white border-gray-200"
        }`}
      >
        {/* Grab handle (phones only) */}
        <div className="flex justify-center pt-2 sm:hidden">
          <div className={`h-1 w-10 rounded-full ${isDarkMode ? "bg-white/20" : "bg-gray-300"}`} />
        </div>

        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center justify-between ${isDarkMode ? "border-white/[0.06]" : "border-gray-100"}`}>
          <div>
            <h2 className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>Product mode</h2>
            <p className={`text-xs ${mutedText}`}>How the products are laid out</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`p-1 rounded-md transition-colors ${isDarkMode ? "hover:bg-white/[0.06] text-gray-500" : "hover:bg-gray-100 text-gray-400"}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3 flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-4">
          {/* Layout */}
          <section>
            <SectionLabel isDarkMode={isDarkMode}>Layout</SectionLabel>
            <div className="flex items-center justify-between gap-3">
              <span className={`text-xs ${bodyText}`}>Show products as</span>
              <Segmented
                options={[
                  { value: "sheet", label: "Sheet" },
                  { value: "grid", label: "Grid" },
                ]}
                value={settings.layout}
                onChange={(layout) => updateSettings({ layout })}
                isDarkMode={isDarkMode}
                ariaLabel="Layout"
              />
            </div>
          </section>

          {/* Image */}
          <section>
            <SectionLabel isDarkMode={isDarkMode}>Image</SectionLabel>
            {imageColumns.length > 1 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                <Chip
                  selected={settings.imageColumnId == null}
                  onClick={() => updateSettings({ imageColumnId: null, imageIndex: 0 })}
                  isDarkMode={isDarkMode}
                >
                  All image columns
                </Chip>
                {imageColumns.map((column) => (
                  <Chip
                    key={column.id}
                    selected={settings.imageColumnId === column.id}
                    onClick={() => updateSettings({ imageColumnId: column.id, imageIndex: 0 })}
                    isDarkMode={isDarkMode}
                  >
                    <ColumnTypeIcon type="image" isDarkMode={isDarkMode} />
                    <span className="truncate max-w-[140px]">{column.name || column.id}</span>
                  </Chip>
                ))}
              </div>
            )}
            {positionCount > 1 && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className={`text-xs ${bodyText}`}>Show the</span>
                <Segmented
                  options={positionOptions}
                  value={selectedPosition}
                  onChange={(index) => updateSettings({ imageIndex: index })}
                  isDarkMode={isDarkMode}
                  ariaLabel="Which image to show"
                />
              </div>
            )}
            <div className="flex items-center justify-between gap-3 mt-2">
              <span className={`text-xs ${bodyText}`}>Fit</span>
              <Segmented
                options={[
                  { value: "cover", label: "Fill" },
                  { value: "contain", label: "Whole image" },
                ]}
                value={settings.imageFit}
                onChange={(fit) => updateSettings({ imageFit: fit })}
                isDarkMode={isDarkMode}
                ariaLabel="Image fit"
              />
            </div>
          </section>

          {/* Grid density */}
          {isGrid && (
            <section>
              <SectionLabel isDarkMode={isDarkMode}>Grid</SectionLabel>
              <div className="flex items-center justify-between gap-3">
                <span className={`text-xs ${bodyText}`}>Products per row</span>
                <Segmented
                  options={PRODUCT_GRID_COLUMN_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
                  value={settings.gridColumns}
                  onChange={(n) => updateSettings({ gridColumns: n })}
                  isDarkMode={isDarkMode}
                  ariaLabel="Products per row"
                />
              </div>
            </section>
          )}

          {/* Fields */}
          <section>
            <SectionLabel isDarkMode={isDarkMode} hint={`${activeIds.length}/${PRODUCT_MODE_MAX_FIELDS}`}>
              {isGrid ? "Details under each image" : "Fields on each row"}
            </SectionLabel>
            <p className={`text-[11px] leading-relaxed mb-2 ${mutedText}`}>
              {isGrid
                ? "Shown in this order under the image."
                : "The first is the title line. The price goes on the right, or the last field when there is no price. A third one sits under the title."}
            </p>
            {fieldColumns.length === 0 ? (
              <p className={`text-xs ${mutedText}`}>This table has no columns to show.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto custom-scrollbar pr-1">
                {fieldColumns.map((column) => {
                  const selected = activeIds.includes(column.id);
                  return (
                    <Chip
                      key={column.id}
                      selected={selected}
                      disabled={!selected && atMaxFields}
                      onClick={() => toggleField(column)}
                      isDarkMode={isDarkMode}
                    >
                      <ColumnTypeIcon type={column.type} isDarkMode={isDarkMode} />
                      <span className="truncate max-w-[140px]">{column.name || column.id}</span>
                      {selected && <CheckIcon />}
                    </Chip>
                  );
                })}
              </div>
            )}
          </section>

          <Switch
            checked={settings.hideWithoutImages}
            onChange={(value) => updateSettings({ hideWithoutImages: value })}
            label="Hide products without images"
            isDarkMode={isDarkMode}
          />

          {/* Full-screen animations */}
          {introSettings && (
            <AnimationSection
              title="Intro animation"
              description="Our logo, “Extracting data…” and the products landing one by one when product mode opens."
              switchLabel="Play when product mode opens"
              rangeLabel="Intro duration in seconds"
              playLabel="Play intro now"
              settings={introSettings}
              onUpdate={updateIntroSettings}
              onPlay={onPlayIntro}
              isDarkMode={isDarkMode}
            />
          )}
          {exportSettings && (
            <AnimationSection
              title="Export animation"
              description="An export card over the sheet — “Exporting N items…” with a progress ring, then a big green check. The file itself downloads once the card is gone."
              switchLabel="Play when exporting"
              rangeLabel="Export animation duration in seconds"
              playLabel="Play export animation now"
              settings={exportSettings}
              onUpdate={updateExportSettings}
              onPlay={onPlayExport}
              isDarkMode={isDarkMode}
            />
          )}

          <p className={`text-[11px] leading-relaxed ${mutedText}`}>
            {isGrid
              ? "Tap a product to open its photos."
              : "Tap a row to see every field it holds, or tap its image to open the photos."}{" "}
            “Use as cover” in the photos picks a different one for just that product.
          </p>
        </div>

        {/* Footer */}
        <div
          className={`px-4 py-3 border-t flex items-center justify-between ${
            isDarkMode ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50/50 border-gray-100"
          }`}
        >
          <button
            type="button"
            onClick={onReset}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/[0.06]" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-500 text-white hover:bg-blue-600 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductModeSettings;
